import { TranslationsContext } from '@teliaee/telia-translate';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Card, CardContent, Separator, Spinner, Text } from 'telia-front-react';
import { v4 } from 'uuid';
import SpinnerWithSpacing from '../components/SpinnerWithSpacing';
import Config from '../Config';
import SearchStore, { EMPLOYEE_CATEGORY, EMPLOYEE_CATEGORY_ARCHIVED } from '../stores/SearchStore';
import DealerSearchResult from './DealerSearchResult';
import SearchErrorNotice from './SearchErrorNotice';
import { DealerNewsFilterOrder, DealerNewsResult, NewsSortOrder, SearchEvent } from './SearchInterfaces';
import SearchNotFoundNotice from './SearchNotFoundNotice';
import ShowMoreButton from './ShowMoreButton';
import SwitchArchiveFilterButton from './SwitchArchiveFilterButton';

interface SearchResultsProps {
  searchStore?: SearchStore;
}

@observer
class DealerSearchResults extends React.Component<SearchResultsProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;

  private isFilteredResultsByTopic(): boolean {
    return [DealerNewsFilterOrder.NEWS, DealerNewsFilterOrder.CAMPAIGNS, DealerNewsFilterOrder.TECHNICIAN, DealerNewsFilterOrder.INSTRUCTIONS].includes(
      this.props.searchStore.searchWrappers[EMPLOYEE_CATEGORY].filterOrder
    );
  }

  private getResults = (isFilteredResultsByTopic: boolean, newsCategory: string): DealerNewsResult[] => {
    const { searchStore } = this.props;
    return isFilteredResultsByTopic ? searchStore.searchWrappers[newsCategory].segmentedResults : searchStore.searchWrappers[newsCategory].results;
  };

  public render() {
    const translate = this.context.translate;
    const { searchStore } = this.props;
    const { status } = searchStore.searchWrappers[EMPLOYEE_CATEGORY];

    if (status === 'UNINITIALIZED') {
      return null;
    }

    if (status === 'FETCHING') {
      return <SpinnerWithSpacing />;
    }

    return (
      <>
        <Card type="employee" background="neutral-bg-level-1" padding="large">
          <CardContent>
            <Text>{this.dealerNewsBlock()}</Text>
            <SwitchArchiveFilterButton category={EMPLOYEE_CATEGORY} labelPrefix="news" />
          </CardContent>
        </Card>
        <SearchErrorNotice showNoticeWhen={['FETCH_MORE_ERROR']} status={status} translate={translate} />
      </>
    );
  }

  private dealerNewsBlock() {
    const translate = this.context.translate;
    const { searchStore } = this.props;

    const categoryFilter = searchStore.searchWrappers[EMPLOYEE_CATEGORY].filterOrder;
    const newsCategory = categoryFilter === DealerNewsFilterOrder.ARCHIVED ? EMPLOYEE_CATEGORY_ARCHIVED : EMPLOYEE_CATEGORY;

    const { status } = searchStore.searchWrappers[newsCategory];
    const results = this.getResults(this.isFilteredResultsByTopic(), newsCategory);

    if (status === 'ERROR') {
      return <SearchErrorNotice showNoticeWhen={['ERROR']} status={status} translate={translate} />;
    }

    if (status === 'REFETCHING') {
      return <Spinner />;
    }

    if (!results.length) {
      const archived: boolean = categoryFilter === DealerNewsFilterOrder.ARCHIVED;
      return <SearchNotFoundNotice customType={archived ? 'newsArchived' : 'dealer'} />;
    }

    return (
      <>
        {this.displayResults(results, newsCategory)}
        <Separator spacing="small" weight="thin" />
        <ShowMoreButton category={newsCategory} />
      </>
    );
  }

  private displayResults(results: DealerNewsResult[], category: string): JSX.Element[] {
    return results.map((result, idx) => {
      return (
        <DealerSearchResult
          result={result}
          key={idx}
          length={results.length}
          onClick={() => this.sendAlgoliaAnalyticsData(result, category)}
          isArchived={category === EMPLOYEE_CATEGORY_ARCHIVED}
        />
      );
    });
  }

  private sendAlgoliaAnalyticsData = async (result: DealerNewsResult, category: string) => {
    const { searchStore } = this.props;
    const sortedByDate = searchStore.searchWrappers[EMPLOYEE_CATEGORY].sortOrder === NewsSortOrder.DATE;
    const idxName = `${Config.componentTier}_dealer${category === EMPLOYEE_CATEGORY_ARCHIVED ? '-archive' : ''}${sortedByDate ? '_date_desc' : ''}`;
    const searchEvent: SearchEvent = {
      eventName: 'Dealer search result click',
      eventType: 'click',
      index: idxName,
      objectIDs: [result.objectID],
      userToken: v4(),
      queryID: searchStore.searchWrappers[category].queryId,
      positions: [searchStore.searchWrappers[category].results.indexOf(result) + 1]
    };

    searchStore.postSearchTabAnalytics();
    searchStore.postSearchResultClickEvent(searchEvent);
  };
}

export default inject('searchStore')(DealerSearchResults);
